/**
 * Cross-unmount / cross-reload persistence for the "Checkfront hold created,
 * capture still pending" state.
 *
 * A single shape is persisted here:
 *   - booking_code (Checkfront hold reference)
 *   - resp (BookingResponse — payment_url etc)
 *   - internal_pay_url (for /pay redirect where applicable)
 *   - capturePayload (immutable Hub agreement payload including the
 *     compact authorization_record JSON; retries send this byte-identical)
 *
 * A separate per-code "capture-success" marker gates the /pay route and
 * map5's direct payment redirect. Pre-capture state alone must never unlock
 * payment — the marker is only written by `markCaptureSuccess` inside the
 * cap.ok branch of the capture retry.
 *
 * Storage lifetime is bounded by TTL_MS (15 min) — matches the Checkfront
 * hold window with a small buffer, so corrupt/expired state fails safely.
 */

import type { CaptureAgreementPayload } from "@/lib/booking/hubGuards";
import type { BookingResponse } from "@/lib/booking/types";

const PENDING_KEY = "srr:pending-capture:v1"; // storage key stable across versions
const SUCCESS_PREFIX = "srr:capture-success:v1:";
const TTL_MS = 15 * 60_000;

export interface PendingCaptureRecord {
  version: 1 | 2;
  property_slug: string;
  booking_code: string;
  resp: BookingResponse;
  internal_pay_url: string | null;
  /**
   * Immutable Hub capture payload. Always present — the checkout flow
   * assembles the full payload (including the authorization_record JSON)
   * before persisting so any reload / remount can retry capture byte-for-byte
   * against the same booking_code without recreating the Checkfront hold.
   */
  capturePayload: CaptureAgreementPayload;
  created_at: number;
  expires_at: number;
}

interface CaptureSuccessRecord {
  version: 1 | 2;
  booking_code: string;
  captured_at: number;
  expires_at: number;
}

const now = () => Date.now();

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function isValidPending(v: unknown): v is PendingCaptureRecord {
  if (!v || typeof v !== "object") return false;
  const r = v as Partial<PendingCaptureRecord>;
  if (r.version !== 1 && r.version !== 2) return false;
  if (typeof r.property_slug !== "string" || !r.property_slug) return false;
  if (typeof r.booking_code !== "string" || !r.booking_code) return false;
  if (typeof r.expires_at !== "number") return false;
  if (typeof r.created_at !== "number") return false;
  if (!r.resp || typeof r.resp !== "object") return false;
  if (!r.capturePayload || typeof r.capturePayload !== "object") return false;
  if (r.capturePayload.booking_code !== r.booking_code) return false;
  if (r.capturePayload.property_slug !== r.property_slug) return false;
  return true;
}

function isValidSuccess(v: unknown): v is CaptureSuccessRecord {
  if (!v || typeof v !== "object") return false;
  const r = v as Partial<CaptureSuccessRecord>;
  return (
    (r.version === 1 || r.version === 2) &&
    typeof r.booking_code === "string" &&
    !!r.booking_code &&
    typeof r.expires_at === "number"
  );
}

// ---------------------------------------------------------------------------
// Pending-capture record
// ---------------------------------------------------------------------------

export function setPendingCapture(input: {
  property_slug: string;
  booking_code: string;
  resp: BookingResponse;
  internal_pay_url: string | null;
  capturePayload: CaptureAgreementPayload;
}): PendingCaptureRecord | null {
  if (typeof window === "undefined") return null;
  const t = now();
  const rec: PendingCaptureRecord = {
    version: 1,
    property_slug: input.property_slug,
    booking_code: input.booking_code,
    resp: input.resp,
    internal_pay_url: input.internal_pay_url,
    capturePayload: input.capturePayload,
    created_at: t,
    expires_at: t + TTL_MS,
  };
  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(rec));
  } catch {
    return null;
  }
  return rec;
}

export function getPendingCapture(opts?: {
  property_slug?: string;
}): PendingCaptureRecord | null {
  if (typeof window === "undefined") return null;
  const raw = window.sessionStorage.getItem(PENDING_KEY);
  const parsed = safeParse<unknown>(raw);
  if (!isValidPending(parsed)) {
    if (raw) clearPendingCapture();
    return null;
  }
  if (parsed.expires_at <= now()) {
    clearPendingCapture();
    return null;
  }
  if (opts?.property_slug && parsed.property_slug !== opts.property_slug) {
    return null;
  }
  return parsed;
}

export function clearPendingCapture(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* noop */
  }
}

// ---------------------------------------------------------------------------
// Post-capture success marker — the ONLY thing that unlocks payment UI.
// ---------------------------------------------------------------------------

function successKey(bookingCode: string): string {
  return `${SUCCESS_PREFIX}${bookingCode}`;
}

export function markCaptureSuccess(bookingCode: string): void {
  if (typeof window === "undefined") return;
  if (!bookingCode) return;
  const t = now();
  const rec: CaptureSuccessRecord = {
    version: 2,
    booking_code: bookingCode,
    captured_at: t,
    expires_at: t + TTL_MS,
  };
  try {
    window.sessionStorage.setItem(successKey(bookingCode), JSON.stringify(rec));
  } catch {
    /* noop */
  }
}

export function hasCaptureSuccess(bookingCode: string | null | undefined): boolean {
  if (typeof window === "undefined") return false;
  if (!bookingCode) return false;
  const raw = window.sessionStorage.getItem(successKey(bookingCode));
  const parsed = safeParse<unknown>(raw);
  if (!isValidSuccess(parsed)) {
    if (raw) clearCaptureSuccess(bookingCode);
    return false;
  }
  if (parsed.expires_at <= now()) {
    clearCaptureSuccess(bookingCode);
    return false;
  }
  return parsed.booking_code === bookingCode;
}

export function clearCaptureSuccess(bookingCode: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(successKey(bookingCode));
  } catch {
    /* noop */
  }
}

// Exposed for tests.
export const __TTL_MS_FOR_TESTS = TTL_MS;
